import React, { useEffect } from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { useBenefits } from '../BenefitsProvider'
import { InlineWidget } from 'react-calendly'
import {
  OrientationCallForm,
  LinkedInReviewForm,
  CallForm,
  ExecutiveBioForm,
  RecruiterGuidanceSupportForm,
  TargetCompanyResearchForm,
  JobSearchStrategyForm,
  InterviewPrepForm,
  NetworkingStrategyForm,
  MockInterviewForm,
  CareerPivotPlanningForm,
  CoverLetterReviewForm,
  ResumeTailoringForm,
  GeneralCheckInForm,
  TargetIndustryResearchForm,
  SalaryResearchForm,
  BusinessCardForm,
  ResumeWritingForm,
  StrengthsAssessmentForm,
} from './RequestForms'
import ResumeReviewForm from './ResumeReviewForm'
import UploadNewResume from './UploadNewResume'
import { Button } from '~/lib/ui/button'

const ScheduleInner = ({ ...props }) => {
  const { page, meetingViaCalendly } = useBenefits()

  return (
    <div className="schedule-dialog">
      {page === 1 && <PageOne {...props} />}
      {page === 2 && meetingViaCalendly && <PageTwo {...props} />}
    </div>
  )
}

const PageTwo = ({ current_user, coach }) => {
  const { t } = useTranslation('schedule_modal')
  const { serviceRequestType, serviceRequestList, meetingDuration } =
    useBenefits()
  const thirtyMinTypes = [
    'ServiceRequest::NetworkingStrategy',
    'ResumeRevision::CandidateRevision',
    'ServiceRequest::ExecutiveBio',
    'ServiceRequest::RecruiterGuidanceSupport',
    'ServiceRequest::BoardSearch',
    'ServiceRequest::JobSearchBurnout',
    'ServiceRequest::ResumeTailoring',
    'ServiceRequest::NegotiationStrategy',
    'ServiceRequest::CoverLetterReview',
    'ServiceRequest::OnboardingSupport',
    'ServiceRequest::ResumeWriting',
  ]

  const meetingLeadTimes = {
    'ServiceRequest::MockInterview': 2,
    'ServiceRequest::BoardSearch': 3,
    'ServiceRequest::ExecutiveBio': 2,
    'ServiceRequest::CareerPivotPlanning': 2,
    'ServiceRequest::JobSearchBurnout': 3,
    'ServiceRequest::ResumeTailoring': 2,
    'ServiceRequest::ResumeWriting': 2,
    'ServiceRequest::GeneralCheckIn': 4,
  }

  const firstOnboardingSupport =
    serviceRequestType === 'ServiceRequest::OnboardingSupport' &&
    !serviceRequestList?.[serviceRequestType]

  const resumeReviewFlex =
    serviceRequestType === 'ServiceRequest::ResumeReview' &&
    (current_user.flex || current_user.silver)

  let computeMeetingDuration
  if (meetingDuration) {
    computeMeetingDuration = meetingDuration
  } else if (
    (thirtyMinTypes.includes(serviceRequestType) && !firstOnboardingSupport) ||
    resumeReviewFlex
  ) {
    computeMeetingDuration = 30
  } else if (serviceRequestType === 'ServiceRequest::StrengthsAssessment') {
    computeMeetingDuration = 90
  } else {
    computeMeetingDuration = 50
  }
  const meetingLeadTime = meetingLeadTimes[serviceRequestType] || ''
  return (
    <div className="flex flex-col gap-6">
      <div className="mb-12">{t('cancellation_policy')}</div>
      <InlineWidget
        url={`${coach.calendly_link}/${computeMeetingDuration}min${meetingLeadTime}`}
        prefill={{
          name: current_user.candidate_name,
          email: current_user.candidate_email,
        }}
      />
    </div>
  )
}

const LiaisonNoInfo = ({ t, select, toggleShowUpdateProfileDialog }) => (
  <>
    <div className="subtitle mb-4">
      {select ? t('liaison_noinfo_select') : t('liaison_noinfo')}
    </div>
    {!select && (
      <Button
        variant="coachcta"
        onClick={() => toggleShowUpdateProfileDialog()}
      >
        {t('update_candidate_profile')}
      </Button>
    )}
  </>
)

const LiaisonDescription = ({ t, select }) => (
  <>
    <div className="subtitle">
      {select ? t('liaison_noinfo_select') : t('liaison_description')}
    </div>
    {select && (
      <Button
        className="mt-3"
        variant="coachcta"
        onClick={() => {
          window.location.href = '/'
        }}
      >
        {t('view_my_dashboard')}
      </Button>
    )}
  </>
)

const IntroCall = ({ t }) => (
  <>
    <div className="subtitle">{t('please_complete_intro_call')}</div>
    <Button
      className="mt-3"
      variant="coachcta"
      onClick={() => {
        window.location.href =
          '/coach_services/professional_services/orientation_call'
      }}
    >
      {t('schedule_coach_intro_call')}
    </Button>
  </>
)

const getServiceRequestComponent = (
  serviceRequestType,
  targetedResearchType,
  t,
) => {
  const map = {
    'ServiceRequest::OrientationCall': <OrientationCallForm />,
    'ServiceRequest::LinkedInReview': <LinkedInReviewForm />,
    'ServiceRequest::ResumeReview': <ResumeReviewForm />,
    'ResumeRevision::CandidateRevision': <ResumeReviewForm />,
    UploadNewResume: <UploadNewResume />,
    'ServiceRequest::ExecutiveBio': <ExecutiveBioForm />,
    'ServiceRequest::RecruiterGuidanceSupport': (
      <RecruiterGuidanceSupportForm />
    ),
    'ServiceRequest::TargetCompanyResearch': <TargetCompanyResearchForm />,
    'ServiceRequest::NetworkingStrategy': <NetworkingStrategyForm />,
    'ServiceRequest::CareerPivotPlanning': <CareerPivotPlanningForm />,
    'ServiceRequest::BoardSearch': <CallForm submitCta={t('submit_request')} />,
    'ServiceRequest::JobSearchStrategy': (
      <JobSearchStrategyForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::InterviewPrep': (
      <InterviewPrepForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::MockInterview': (
      <MockInterviewForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::CoverLetterReview': (
      <CoverLetterReviewForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::ResumeTailoring': (
      <ResumeTailoringForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::GeneralCheckIn': (
      <GeneralCheckInForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::SalaryResearch': (
      <SalaryResearchForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::BusinessCardCreation': (
      <BusinessCardForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::ResumeWriting': (
      <ResumeWritingForm submitCta={t('submit_request')} />
    ),
    'ServiceRequest::StrengthsAssessment': <StrengthsAssessmentForm />,
  }

  if (
    serviceRequestType === 'ServiceRequest::TargetCompanyResearch' ||
    targetedResearchType === 'company'
  ) {
    return <TargetCompanyResearchForm />
  }
  if (targetedResearchType === 'industry' || targetedResearchType === 'both') {
    return <TargetIndustryResearchForm submitCta={t('submit_request')} />
  }
  return map[serviceRequestType] || <CallForm />
}

const PageOne = ({ toggleShowUpdateProfileDialog }) => {
  const {
    serviceRequestType,
    isPremium,
    setMeetingViaCalendly,
    coach,
    profileIncomplete,
    targetedResearchType,
    select,
    current_user,
    // toggleShowUpdateProfileDialog,
  } = useBenefits()
  const { t } = useTranslation('schedule_modal')

  const isLiaison = coach?.isLiaison || false
  const introCallCompleted = current_user.orientationCallCompleted

  useEffect(() => {
    if (
      isPremium &&
      (isLiaison || !introCallCompleted) &&
      serviceRequestType === 'ServiceRequest::TargetCompanyResearch'
    ) {
      setMeetingViaCalendly(false)
    }
  }, [
    isPremium,
    isLiaison,
    serviceRequestType,
    setMeetingViaCalendly,
    introCallCompleted,
  ])

  if ((isPremium || select) && isLiaison && profileIncomplete)
    return (
      <LiaisonNoInfo
        t={t}
        select={select}
        toggleShowUpdateProfileDialog={toggleShowUpdateProfileDialog}
      />
    )

  if ((isPremium || select) && isLiaison && !profileIncomplete)
    return <LiaisonDescription t={t} select={select} />

  if (
    (isPremium || select) &&
    !introCallCompleted &&
    serviceRequestType !== 'ServiceRequest::OrientationCall'
  ) {
    return <IntroCall t={t} />
  }

  return getServiceRequestComponent(serviceRequestType, targetedResearchType, t)
}

export default ScheduleInner
